<script setup lang="ts">
  import { onMounted, useTemplateRef } from 'vue'
  import {
    DataList,
    DataListItem,
    DataListItemTitle,
    Button
  } from '@gat-ship/vue-components'
  import { IconLayers, IconLoading, IconPlus } from '@gat-ship/icons'
  import type { PortCallTypeRef } from '@gat-ship/types'
  import { usePortCallTypes } from '../composables/usePortCallTypes'
  import PortCallTypeModalAdd from './providers/portcall-types-provider/modals/PortCallTypeModalAdd.vue'
  import PortCallTypeModalEdit from './providers/portcall-types-provider/modals/PortCallTypeModalEdit.vue'

  const service = usePortCallTypes()

  const addModal =
    useTemplateRef<InstanceType<typeof PortCallTypeModalAdd>>('addModal')
  const editModal =
    useTemplateRef<InstanceType<typeof PortCallTypeModalEdit>>('editModal')

  onMounted(() => {
    void service.fetch()
  })

  function openAdd() {
    addModal.value?.showModal()
  }

  function openEdit(entry: PortCallTypeRef) {
    editModal.value?.showModal({ entry })
  }

  // Hosts (the administration modal panel) render their own "Add" affordance in
  // their chrome and forward it here.
  defineExpose({ openAdd })
</script>

<template>
  <div
    v-if="service.isLoading.portCallTypes"
    class="text-muted-foreground mx-auto flex animate-pulse items-center justify-center gap-2 p-6"
  >
    <IconLoading class="size-6" /> Loading…
  </div>

  <div
    v-else-if="service.portCallTypes.value.length === 0"
    class="text-muted-foreground mx-auto flex flex-col items-center gap-2 p-10 text-center"
  >
    <IconLayers class="size-8 opacity-60" />
    <div class="text-sm">No portcall types yet.</div>
    <Button size="lg" @click="openAdd">
      <IconPlus class="size-4" />
      Add portcall type
    </Button>
  </div>

  <DataList v-else>
    <DataListItem
      v-for="entry in service.portCallTypes.value"
      :key="entry.id"
      @click:item="openEdit(entry)"
    >
      <div class="flex w-full items-center gap-3">
        <IconLayers class="text-muted-foreground size-4 shrink-0" />
        <DataListItemTitle>
          <span class="text-sm font-medium">{{ entry.name }}</span>
        </DataListItemTitle>
      </div>
    </DataListItem>
  </DataList>

  <PortCallTypeModalAdd ref="addModal" />
  <PortCallTypeModalEdit ref="editModal" />
</template>
